import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle, Quotes, ArrowSquareOut, PlayCircle, CaretLeft, CaretRight, Users, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import ActionPortalMiniApp from "@/pages/demos/ActionPortalMiniApp";
import InterestModal from "@/components/InterestModal";
import { useSiteSettings } from "@/context/SiteSettingsContext";

export default function DemoDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { settings } = useSiteSettings();
  const [demo, setDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [interestOpen, setInterestOpen] = useState(false);
  const [activeScreen, setActiveScreen] = useState(0);
  const [isHoveringHero, setIsHoveringHero] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/demos/${slug}`)
      .then(({ data }) => setDemo(data))
      .catch(() => setDemo(null))
      .finally(() => setLoading(false));
  }, [slug]);

  // Auto-rotate hero carousel every 5s; pause on hover
  useEffect(() => {
    if (!demo || isHoveringHero) return;
    const n = demo.screens?.length || 0;
    if (n <= 1) return;
    const id = setInterval(() => setActiveScreen((i) => (i + 1) % n), 5000);
    return () => clearInterval(id);
  }, [demo, isHoveringHero]);

  const registerInterest = () => setInterestOpen(true);

  if (loading) return <div className="app-container py-32 text-center font-mono text-zinc-500" data-testid="demo-loading">Loading case study…</div>;
  if (!demo) {
    return (
      <div className="app-container py-32 text-center" data-testid="demo-not-found">
        <h1 className="text-3xl font-bold mb-4">Case study not found</h1>
        <Link to="/demos" className="btn-ghost-ink">Back to catalogue</Link>
      </div>
    );
  }

  const isConcept = (demo.tags || []).includes("concept");
  const isFlagship = demo.slug === "holborough-social";
  const typeLabel = {
    case_study: "Case study",
    clickable: "Clickable prototype",
    mini_app: "Interactive mini-app",
    interactive: isFlagship ? "Interactive flagship" : isConcept ? "Interactive concept" : "Interactive demo",
    live: "Live app — try it free",
  }[demo.demo_type] || "Case study";

  const screens = demo.screens?.length ? demo.screens : (demo.media_url ? [{ url: demo.media_url, caption: demo.title }] : []);
  const goPrev = () => setActiveScreen((i) => (i - 1 + screens.length) % screens.length);
  const goNext = () => setActiveScreen((i) => (i + 1) % screens.length);

  return (
    <div data-testid="demo-detail-page">
      <div className="app-container pt-10">
        <Link to="/demos" className="inline-flex items-center gap-2 text-sm text-zinc-500 hover:text-zinc-900" data-testid="back-to-catalogue">
          <ArrowLeft size={14} /> Catalogue
        </Link>
      </div>

      {/* HERO */}
      <section className="app-container py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7">
          <div className="flex items-center gap-3 mb-4 flex-wrap">
            <span className={`text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 ${demo.demo_type === "live" ? "bg-[hsl(var(--appistry-signal))] text-white border border-[hsl(var(--appistry-signal))]" : "text-[hsl(var(--appistry-signal))] border border-[hsl(var(--appistry-signal))]"}`} data-testid="demo-type-badge">
              {typeLabel}
            </span>
            {demo.co_innovation && settings.co_innovation_enabled && (
              <Link to="/co-innovation" className="text-[10px] font-mono uppercase tracking-[0.2em] px-2 py-1 bg-zinc-950 text-white hover:bg-zinc-800 transition-colors" data-testid="demo-coinnovation-badge">
                ★ Co-innovation candidate
              </Link>
            )}
            <span className="overline text-zinc-500">{demo.category}</span>
            {demo.client && <span className="text-xs font-mono text-zinc-500">· {demo.client}</span>}
            {demo.timeframe && <span className="text-xs font-mono text-zinc-500">· {/^(Live|Launched|Running|Since)/i.test(demo.timeframe) ? demo.timeframe : `Built in ${demo.timeframe}`}</span>}
          </div>
          <h1 className="text-5xl md:text-6xl font-black tracking-tighter leading-none mb-6" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="demo-title">
            {demo.title}
          </h1>
          <p className="text-xl text-zinc-600 leading-relaxed mb-8 max-w-2xl" data-testid="demo-short-description">{demo.short_description}</p>
          <div className="flex flex-wrap gap-3">
            {demo.demo_type === "interactive" && isFlagship && demo.live_url && (
              <a href={demo.live_url} target="_blank" rel="noreferrer" className="btn-signal" data-testid="demo-try-live-hero">
                <PlayCircle size={18} weight="fill" /> Sign up on the live app <ArrowSquareOut size={14} weight="bold" />
              </a>
            )}
            {demo.demo_type === "interactive" && !isFlagship && (
              <button onClick={() => navigate(`/demos/${demo.slug}/try`)} className="btn-signal" data-testid="demo-try-interactive">
                <PlayCircle size={18} weight="fill" /> Try the interactive demo <ArrowRight size={16} weight="bold" />
              </button>
            )}
            {demo.demo_type === "mini_app" && (
              <a href="#mini-app" className="btn-signal" data-testid="demo-try-mini-app">
                <PlayCircle size={18} weight="fill" /> Try it below <ArrowRight size={16} weight="bold" />
              </a>
            )}
            {demo.demo_type === "live" && demo.live_url && (
              <a href={demo.live_url} target="_blank" rel="noreferrer" className="btn-signal" data-testid="demo-try-live">
                <PlayCircle size={18} weight="fill" /> Use the live app — free <ArrowSquareOut size={14} weight="bold" />
              </a>
            )}
            {demo.live_url && demo.demo_type !== "live" && !isFlagship && (
              <a href={demo.live_url} target="_blank" rel="noreferrer" className="btn-ghost-ink" data-testid="demo-live-link">
                Visit the live app <ArrowSquareOut size={14} weight="bold" />
              </a>
            )}
            <Link to={`/consultation?demo=${demo.slug}`} className="btn-ghost-ink" data-testid="demo-consultation-cta">
              Want one like this? <ArrowRight size={14} weight="bold" />
            </Link>
          </div>
        </div>
        <div className="lg:col-span-5">
          {screens.length > 0 && (
            <div
              className="relative border border-zinc-200 bg-white overflow-hidden group"
              onMouseEnter={() => setIsHoveringHero(true)}
              onMouseLeave={() => setIsHoveringHero(false)}
              data-testid="demo-hero-carousel"
            >
              <img
                key={activeScreen}
                src={screens[activeScreen].url}
                alt={screens[activeScreen].alt || screens[activeScreen].caption}
                className="w-full aspect-[4/3] object-contain bg-zinc-50 p-3 md:p-4 animate-[fadeIn_400ms_ease-out]"
                data-testid="demo-hero-screen"
              />
              {screens.length > 1 && (
                <>
                  <button
                    onClick={goPrev}
                    aria-label="Previous screen"
                    className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 bg-white/90 hover:bg-white border border-zinc-200 shadow-sm flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity"
                    data-testid="demo-hero-prev"
                  >
                    <CaretLeft size={18} weight="bold" />
                  </button>
                  <button
                    onClick={goNext}
                    aria-label="Next screen"
                    className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 bg-white/90 hover:bg-white border border-zinc-200 shadow-sm flex items-center justify-center opacity-70 group-hover:opacity-100 transition-opacity"
                    data-testid="demo-hero-next"
                  >
                    <CaretRight size={18} weight="bold" />
                  </button>
                  <div className="absolute top-3 right-3 text-[10px] font-mono uppercase tracking-widest bg-zinc-950/80 text-white px-2 py-1" data-testid="demo-hero-counter">
                    {String(activeScreen + 1).padStart(2, "0")} / {String(screens.length).padStart(2, "0")}
                  </div>
                  <div className="flex gap-1.5 p-3 border-t border-zinc-200 bg-zinc-50">
                    {screens.map((s, i) => (
                      <button
                        key={i}
                        onClick={() => setActiveScreen(i)}
                        aria-label={`Go to screen ${i + 1}`}
                        className={`flex-1 h-2 transition-colors ${i === activeScreen ? "bg-[hsl(var(--appistry-signal))]" : "bg-zinc-300 hover:bg-zinc-400"}`}
                        data-testid={`demo-screen-dot-${i}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
          {screens[activeScreen]?.caption && (
            <p className="text-xs font-mono text-zinc-600 mt-3 leading-relaxed" data-testid="demo-hero-caption">
              <span className="text-zinc-400">{String(activeScreen + 1).padStart(2, "0")} ·</span> {screens[activeScreen].caption}
            </p>
          )}
        </div>
      </section>

      {/* METRICS BAR */}
      {demo.metrics && demo.metrics.length > 0 && (
        <section className="border-y border-zinc-200 bg-white" data-testid="demo-metrics">
          <div className="app-container">
            <div className="grid grid-cols-2 md:grid-cols-4 divide-x divide-zinc-200 border-x border-zinc-200">
              {demo.metrics.map((m, i) => (
                <div key={i} className="px-6 py-8">
                  <div className="overline text-zinc-500 mb-2">{m.label}</div>
                  <div className="text-3xl md:text-4xl font-black tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{m.value}</div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* PROBLEM / SOLUTION */}
      <section className="border-b border-zinc-200">
        <div className="app-container py-16 md:py-24 grid grid-cols-1 md:grid-cols-2 gap-10">
          <div>
            <p className="overline mb-4">The problem</p>
            <p className="text-xl text-zinc-800 leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="demo-problem">
              {demo.problem}
            </p>
          </div>
          <div>
            <p className="overline mb-4">What we built</p>
            <p className="text-xl text-zinc-800 leading-relaxed" style={{ fontFamily: "'Outfit', sans-serif" }} data-testid="demo-solution">
              {demo.solution}
            </p>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      {demo.features?.length > 0 && (() => {
        // Split features by Residents/Committee/HLSC prefix (case-sensitive on supported demos)
        const stripPrefix = (f) =>
          f.replace(/^Residents\s+[—–-]\s+/, "")
            .replace(/^HLSC\s+[—–-]\s+/, "")
            .replace(/^Committee\s+[—–-]\s+/, "");
        const residents = demo.features.filter((f) => /^Residents\s+[—–-]\s+/.test(f)).map(stripPrefix);
        const committee = demo.features.filter((f) => /^(HLSC|Committee)\s+[—–-]\s+/.test(f)).map(stripPrefix);
        const isSplit = residents.length > 0 && committee.length > 0;
        const usesHLSC = demo.features.some((f) => /^HLSC\s+[—–-]\s+/.test(f));
        const adminLabel = usesHLSC ? "For HLSC" : "For the committee";
        const adminSubtext = usesHLSC ? "HLSC's" : "the committee's";

        return (
          <section className="border-b border-zinc-200 bg-white">
            <div className="app-container py-16 md:py-20">
              <p className="overline mb-6">What it does</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-3 max-w-2xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Features, no fluff.
              </h2>
              {isSplit && (
                <p className="text-zinc-600 max-w-2xl mb-10 leading-relaxed">
                  What {residents.length > 1 ? "residents" : "a resident"} sees on the left; what stays on {adminSubtext} private side on the right.
                </p>
              )}

              {isSplit ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-zinc-200">
                  {/* Residents column */}
                  <div className="p-6 md:p-8 border-b lg:border-b-0 lg:border-r border-zinc-200 bg-white">
                    <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6 pb-3 border-b border-zinc-200">
                      <div className="flex items-center gap-2">
                        <Users size={20} weight="duotone" className="text-[hsl(var(--appistry-signal))]" />
                        <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>For residents</h3>
                      </div>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{residents.length} · public</p>
                    </div>
                    <ul className="space-y-3.5">
                      {residents.map((f, i) => (
                        <li key={i} className="flex gap-3 items-start" data-testid={`feature-residents-${i}`}>
                          <CheckCircle size={18} weight="fill" className="text-[hsl(var(--appistry-signal))] flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-800 leading-snug text-sm md:text-[15px]">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {/* Admin (committee/HLSC) column */}
                  <div className="p-6 md:p-8 bg-zinc-950 text-white">
                    <div className="flex items-baseline justify-between flex-wrap gap-2 mb-6 pb-3 border-b border-zinc-800">
                      <div className="flex items-center gap-2">
                        <ShieldCheck size={20} weight="duotone" className="text-[hsl(var(--appistry-signal))]" />
                        <h3 className="text-xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>{adminLabel}</h3>
                      </div>
                      <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{committee.length} · private</p>
                    </div>
                    <ul className="space-y-3.5">
                      {committee.map((f, i) => (
                        <li key={i} className="flex gap-3 items-start" data-testid={`feature-committee-${i}`}>
                          <CheckCircle size={18} weight="fill" className="text-[hsl(var(--appistry-signal))] flex-shrink-0 mt-0.5" />
                          <span className="text-zinc-200 leading-snug text-sm md:text-[15px]">{f}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border-t border-l border-zinc-200">
                  {demo.features.map((f, i) => (
                    <div key={i} className="p-6 border-r border-b border-zinc-200 flex gap-4 items-start bg-white" data-testid={`feature-${i}`}>
                      <CheckCircle size={22} weight="fill" className="text-[hsl(var(--appistry-signal))] flex-shrink-0 mt-0.5" />
                      <p className="text-zinc-800 leading-relaxed">{f}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>
        );
      })()}

      {/* INLINE MINI-APP (for mini_app type) */}
      {demo.demo_type === "mini_app" && demo.interactive_slug === "action-portal" && (
        <section id="mini-app" className="border-b border-zinc-200 bg-zinc-50">
          <div className="app-container py-16 md:py-20">
            <div className="flex items-center justify-between flex-wrap gap-3 mb-8">
              <div>
                <p className="overline mb-2 text-[hsl(var(--appistry-signal))]">Live mini-app</p>
                <h2 className="text-3xl md:text-4xl font-bold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>Try it — no sign-up needed.</h2>
                <p className="text-zinc-600 mt-2 max-w-xl">Your data stays in your browser session. Reset any time.</p>
              </div>
            </div>
            <ActionPortalMiniApp />
          </div>
        </section>
      )}

      {/* CLICKABLE PROTOTYPE / LIVE APP (screens gallery + iframe preview link) */}
      {(demo.demo_type === "clickable" || demo.demo_type === "live") && (
        <section className="border-b border-zinc-200 bg-zinc-50">
          <div className="app-container py-16 md:py-20">
            <p className="overline mb-4">{demo.demo_type === "live" ? "It's live — not a mock-up" : "Walk through it"}</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              {demo.demo_type === "live" ? "A real, production app you can use today." : "Have a click around — it's live."}
            </h2>
            <p className="text-zinc-600 mb-10 max-w-xl">
              Every screen below is from the real, running app. {demo.slug === "reflective-mind" ? "You can sign up and use it for free right now — takes 30 seconds, no credit card." : "Open the live app to explore it properly."}
            </p>
            {demo.live_url && (
              <div className="mb-10 bg-gradient-to-br from-[hsl(var(--appistry-signal))] to-[hsl(var(--appistry-teal))] text-white p-6 md:p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                <div>
                  <div className="overline text-white/80 mb-2">{demo.slug === "reflective-mind" ? "Free to try · public access" : "Live & hosted"}</div>
                  <h3 className="text-2xl md:text-3xl font-black tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {demo.slug === "reflective-mind" ? "Sign up free and start reflecting in 30 seconds." : `Open ${demo.title} in a new tab.`}
                  </h3>
                </div>
                <a href={demo.live_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-white text-[hsl(var(--appistry-signal))] px-5 py-3 font-semibold whitespace-nowrap hover:bg-zinc-100 transition-colors" data-testid="clickable-open-live">
                  {demo.slug === "reflective-mind" ? "Start free" : "Open the app"}
                  <ArrowSquareOut size={16} weight="bold" />
                </a>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              {screens.map((s, i) => (
                <figure key={i} className="border border-zinc-200 bg-white overflow-hidden group" data-testid={`clickable-screen-${i}`}>
                  <img src={s.url} alt={s.alt || s.caption} className="w-full aspect-[16/10] object-cover object-top group-hover:scale-[1.02] transition-transform duration-500" />
                  <figcaption className="px-4 py-3 border-t border-zinc-200 text-xs font-mono text-zinc-600 flex items-center justify-between">
                    <span>0{i + 1} · {s.caption}</span>
                    {demo.live_url && (
                      <a href={demo.live_url} target="_blank" rel="noreferrer" className="text-[hsl(var(--appistry-signal))] hover:underline inline-flex items-center gap-1">
                        Open <ArrowSquareOut size={12} />
                      </a>
                    )}
                  </figcaption>
                </figure>
              ))}
              {screens.length < 4 && (
                <div className="border border-dashed border-zinc-300 bg-white/50 p-8 flex flex-col items-center justify-center text-center text-zinc-400 aspect-[16/10]" data-testid="clickable-placeholder">
                  <div className="text-xs font-mono uppercase tracking-widest mb-2">More screens coming</div>
                  <p className="text-sm max-w-xs">Drop extra screenshots from the live app into this case study via Admin → Demos.</p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* INTERACTIVE (teaser — full app lives at /demos/:slug/try, flagship gets live-app CTA first) */}
      {demo.demo_type === "interactive" && (() => {
        const overline = isFlagship ? "It's live — sign up & explore" : isConcept ? "Interactive concept" : "Interactive demo";
        const headline = isFlagship ? "It's live. Have a look around." : isConcept ? "Play with the concept." : "Try it yourself.";
        const body = isFlagship
          ? `${demo.title} is the real app the Holborough Lakes community uses every day — not a mock-up. Sign up as a resident to browse events, buy tickets, read news, volunteer, and find HLSC-vetted local vendors, exactly like they do. HLSC's admin tools stay private — you'll see what those look like in the "Inside the app" tour just below.`
          : isConcept
            ? `This is a fully interactive concept — click around, change things, see it react. If the shape fits your business, we can build the real version connected to your data in a few weeks.`
            : `A working, interactive demo. Click around to see how it behaves.`;
        const cta = isConcept ? "Play with the concept" : "Open the demo";
        return (
        <section className="border-b border-zinc-200 bg-zinc-950 text-white relative overflow-hidden">
          <div className="absolute inset-0 grid-dots opacity-5" />
          <div className="relative app-container py-20 grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
            <div className="md:col-span-7">
              <p className="overline text-[hsl(var(--appistry-signal))] mb-4">{overline}</p>
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {headline}
              </h2>
              <p className="text-zinc-300 leading-relaxed max-w-lg mb-8">
                {body}
              </p>
              {isFlagship && demo.live_url ? (
                <a href={demo.live_url} target="_blank" rel="noreferrer" className="btn-signal" data-testid="interactive-open-live">
                  <PlayCircle size={18} weight="fill" /> Sign up on the live app <ArrowSquareOut size={14} weight="bold" />
                </a>
              ) : (
                <button onClick={() => navigate(`/demos/${demo.slug}/try`)} className="btn-signal" data-testid="interactive-launch">
                  <PlayCircle size={18} weight="fill" /> {cta} <ArrowRight size={16} weight="bold" />
                </button>
              )}
            </div>
            <div className="md:col-span-5">
              <div className="border border-zinc-800 bg-zinc-900 overflow-hidden">
                <img src={screens[0]?.url || demo.media_url} alt={demo.media_alt || demo.title} className="w-full aspect-[4/3] object-cover object-top opacity-90" />
              </div>
            </div>
          </div>
        </section>
        );
      })()}

      {/* FOR VENDORS & SPONSORS GALLERY (resident-facing "sub-app" for sellers) */}
      {demo.vendor_screens?.length > 0 && (
        <section className="border-b border-zinc-200 bg-white">
          <div className="app-container py-16 md:py-20">
            <p className="overline mb-4">For vendors & sponsors</p>
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
              A sub-app for the people who sell.
            </h2>
            <p className="text-zinc-600 mb-10 max-w-2xl">
              Local vendors and sponsors get their own logged-in area. They manage their profile, see upcoming events they're confirmed for, and apply for future pitches themselves — no more email-tennis with HLSC.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="demo-vendor-gallery">
              {demo.vendor_screens.map((s, i) => (
                <figure key={i} className="border border-zinc-200 bg-zinc-50 overflow-hidden group" data-testid={`vendor-screen-${i}`}>
                  <img src={s.url} alt={s.alt || s.caption} className="w-full aspect-[16/10] object-contain bg-white group-hover:scale-[1.02] transition-transform duration-500" loading="lazy" />
                  <figcaption className="px-4 py-3 border-t border-zinc-200 text-xs font-mono text-zinc-600 bg-white">
                    <span className="text-zinc-400">0{i + 1}</span> · {s.caption}
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* INSIDE THE APP GALLERY (flagship interactive demos with rich screens — admin surfaces guests can't see) */}
      {demo.demo_type === "interactive" && isFlagship && ((demo.admin_screens?.length > 0) || screens.length > 1) && (() => {
        const adminScreens = demo.admin_screens?.length > 0 ? demo.admin_screens : screens;
        return (
          <section className="border-b border-zinc-200 bg-zinc-50">
            <div className="app-container py-16 md:py-20">
              <p className="overline mb-4">Inside the app · private to HLSC</p>
              <h2 className="text-3xl md:text-4xl font-bold tracking-tight mb-4" style={{ fontFamily: "'Outfit', sans-serif" }}>
                What guests don't see.
              </h2>
              <p className="text-zinc-600 mb-10 max-w-2xl">
                Residents and vendors have their own views (above). Everything below is HLSC-only — the admin workbench that ran Easter, summer fetes and the Business Networking evening without a single spreadsheet.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="demo-inside-gallery">
                {adminScreens.map((s, i) => (
                  <figure key={i} className="border border-zinc-200 bg-white overflow-hidden group" data-testid={`inside-screen-${i}`}>
                    <img src={s.url} alt={s.alt || s.caption} className="w-full aspect-[16/10] object-cover object-top group-hover:scale-[1.02] transition-transform duration-500" loading="lazy" />
                    <figcaption className="px-4 py-3 border-t border-zinc-200 text-xs font-mono text-zinc-600">
                      <span className="text-zinc-400">0{i + 1}</span> · {s.caption}
                    </figcaption>
                  </figure>
                ))}
              </div>
            </div>
          </section>
        );
      })()}

      {/* TESTIMONIAL */}
      {demo.testimonial && (
        <section className="border-b border-zinc-200 bg-white">
          <div className="app-container py-20 md:py-24" data-testid="demo-testimonial">
            <Quotes size={48} weight="fill" className="text-[hsl(var(--appistry-signal))] mb-6" />
            <blockquote className="text-2xl md:text-3xl font-medium tracking-tight leading-snug max-w-3xl" style={{ fontFamily: "'Outfit', sans-serif" }}>
              "{demo.testimonial.quote}"
            </blockquote>
            <div className="mt-8 flex items-center gap-4">
              {demo.testimonial.logo ? (
                <div className="h-16 w-16 bg-white border border-zinc-200 flex items-center justify-center p-1.5">
                  <img src={demo.testimonial.logo} alt={`${demo.testimonial.author} logo`} className="max-h-full max-w-full object-contain" />
                </div>
              ) : (
                <div className="h-10 w-10 bg-zinc-950 flex items-center justify-center">
                  <span className="text-white font-black text-sm tracking-tighter" style={{ fontFamily: "'Outfit', sans-serif" }}>
                    {demo.testimonial.author?.[0] || "H"}
                  </span>
                </div>
              )}
              <div>
                <div className="font-semibold text-zinc-950">{demo.testimonial.author}</div>
                {demo.testimonial.role && (
                  <div className="text-sm text-zinc-500">{demo.testimonial.role}</div>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FINAL CTA */}
      <section className="bg-zinc-950 text-white">
        <div className="app-container py-16 md:py-20 text-center">
          <p className="overline text-zinc-500 mb-4">Ready to talk?</p>
          <h2 className="text-3xl md:text-5xl font-black tracking-tighter mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Want something like {demo.title}, shaped around your team?
          </h2>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to={`/consultation?demo=${demo.slug}`} className="btn-signal" data-testid="demo-bottom-consultation">
              Talk to us about this <ArrowRight size={16} weight="bold" />
            </Link>
            <button onClick={registerInterest} className="btn-ghost-ink !text-white !border-white/30 hover:!bg-white/10" data-testid="demo-bottom-interest">
              Just register interest
            </button>
          </div>
        </div>
      </section>

      <InterestModal
        open={interestOpen}
        onClose={() => setInterestOpen(false)}
        source={`interest_${demo.slug}`}
        demoTitle={demo.title}
        ctaLabel="Register interest"
      />
    </div>
  );
}
